import type { Schemas } from "@/shared/api/client";
import type { Locale, MessageId } from "@/shared/i18n/messages";

export type SentenceValues = Record<string, string | number>;

/** Translate a message id: useT() in components, a catalog formatter in tests. */
export type Translate = (id: MessageId, values?: SentenceValues) => string;

/**
 * How {target} reads in a sentence:
 * - "reference": a task reference such as NOON-12, always laid out left to right,
 * - "name": a project's name, in its own direction,
 * - "phrase": words from the catalog, such as "this task".
 */
export type TargetKind = "reference" | "name" | "phrase";

/**
 * One line of the activity feed, the task timeline or the notifications: a catalog message and
 * its values. The values are plain text (labels already translated), so the same sentence can
 * be read aloud as a string or rendered with the actor and target emphasised (RichSentence).
 */
export interface Sentence {
  id: MessageId;
  values: SentenceValues;
  targetKind: TargetKind;
}

type Person = Schemas["PersonOut"];
type TaskStatus = Schemas["TaskStatus"];

const TASK_STATUSES: readonly string[] = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "cancelled",
] satisfies readonly TaskStatus[];

export function isTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === "string" && TASK_STATUSES.includes(value);
}

/** A text value of an activity or notification payload, or null when absent or not text. */
export function payloadText(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** Who did it; "Someone" when the server no longer knows (a removed account, the system). */
export function actorName(actor: Person | null, translate: Translate): string {
  return actor?.name ?? translate("activity.someone");
}

export interface SentenceContext {
  translate: Translate;
  locale: Locale;
  /** Names of people a payload refers to by id only (assignees, new members). */
  people: ReadonlyMap<string, string>;
  /** The signed-in person, so "assigned to you" reads as such. */
  currentUserId: string | null;
  /** "feed": tasks are named by their reference; "task": inside a task, it is "this task". */
  scope: "feed" | "task";
}
